// =============================================================================
// SAFE STORAGE — every Web Storage access that must not be able to take a page down.
//
// Flat next to App.js, tracking.js and affiliateRef.js. There is no src/utils/ and
// no src/services/ in this app; flat modules beside App.js are the house shape.
//
// 🔴 THE BUG THIS EXISTS TO KILL. `localStorage` is a GETTER on `window`, and in
//    Safari private browsing and Chrome with "block all cookies" that getter
//    THROWS — it does not return null and it does not return an empty store:
//
//        SecurityError: The operation is insecure.
//
//    So `localStorage.getItem('token')` throws on the `localStorage` half, before
//    getItem is ever reached. Read it in a constructor or a render() and React
//    rethrows, the tree unmounts, and the visitor gets a WHITE PAGE before anything
//    paints. On `/` that means the landing page every tracked link points at simply
//    does not exist for those visitors — and `src/tracking.js` is inside the bundle
//    that never ran, so they are not even counted as a loss.
//
// 🔒 THE CONTRACT: A MISSING VALUE AND A THROWING STORE ARE INDISTINGUISHABLE.
//    Reads return the string or `null`. Writes and removes return a boolean saying
//    whether the store took it, and are safe to ignore. Nothing here throws, ever.
//    A caller written against these functions cannot be the reason a page dies.
//
// ⚠️ THE WHOLE EXPRESSION SITS INSIDE THE try. `try { window.localStorage.getItem(k) }`
//    is correct; hoisting `const ls = window.localStorage` outside the try is the
//    bug, because that line is the one that throws.
//
// WHY THIS FILE AND NOT tracking.js: tracking.js has its own private copies of these
// (readLocal/writeLocal/readSession/…) and its header declares it touches none of the
// session keys. It is deployed, load-bearing for the link dashboard, and correct.
// Rewiring it to import from here would put the double-count guard and the
// once-per-session rule at risk for no user-visible gain. Same for affiliateRef.js,
// whose accessors are frozen alongside the 365-day rule and four live revenue-share
// relationships. New call sites come here; those two stay as they are.
// =============================================================================

/**
 * A localStorage value, or null. Null means "no value" whether the key is absent,
 * the store is empty, or the store threw. Never throws.
 */
export function readLocal(key) {
    try { return window.localStorage.getItem(key); } catch (_) { return null; /* storage disabled */ }
}

/**
 * Persist a localStorage value. Returns false when the store refused it (disabled,
 * or over quota) so the value simply is not persisted. Never throws.
 */
export function writeLocal(key, value) {
    try { window.localStorage.setItem(key, value); return true; } catch (_) { return false; /* storage disabled */ }
}

/**
 * Remove a localStorage key. Returns false when the store threw. Never throws.
 */
export function removeLocal(key) {
    try { window.localStorage.removeItem(key); return true; } catch (_) { return false; /* storage disabled */ }
}

/**
 * A sessionStorage value, or null. Same contract as readLocal. Never throws.
 */
export function readSession(key) {
    try { return window.sessionStorage.getItem(key); } catch (_) { return null; /* storage disabled */ }
}

/**
 * Persist a sessionStorage value. Same contract as writeLocal. Never throws.
 */
export function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); return true; } catch (_) { return false; /* storage disabled */ }
}

/**
 * Remove a sessionStorage key. Same contract as removeLocal. Never throws.
 */
export function removeSession(key) {
    try { window.sessionStorage.removeItem(key); return true; } catch (_) { return false; /* storage disabled */ }
}


// -----------------------------------------------------------------------------
// AVAILABILITY PROBE — used to STOP a signup, so it has to be right.
//
// 🔴 WHY A `typeof` CHECK WILL NOT DO. Every failing environment we care about
//    still presents the API:
//      · Safari private browsing / Chrome "block all cookies" — `window.localStorage`
//        is there, and THROWS when you touch it.
//      · A store at quota 0 — `setItem` returns without error and stores nothing.
//    `typeof window.localStorage !== 'undefined'` is true in both. So this does a
//    real round trip: write a throwaway key, READ IT BACK, remove it. Only a store
//    that actually returns what it was given passes.
//
// 🔒 The probe key is namespaced and removed immediately; it is not one of the
//    frozen keys (`token`, `user`, `upcoming_event`, `affiliate_ref`,
//    `signupFormData`, `from_signup`) and nothing else ever reads it.
// -----------------------------------------------------------------------------

const PROBE_KEY = '__am_storage_probe__';

/**
 * True only when localStorage genuinely persists a value and hands it back.
 * Never throws. Call it once per page load — three storage ops is not free.
 */
export function storageAvailable() {
    try {
        window.localStorage.setItem(PROBE_KEY, '1');
        const readBack = window.localStorage.getItem(PROBE_KEY);
        window.localStorage.removeItem(PROBE_KEY);
        return readBack === '1';
    } catch (_) {
        return false; // storage disabled — the whole expression can throw, not just the call
    }
}

/**
 * Shown by the three funnels that CREATE AN ACCOUNT when the probe fails.
 *
 * 🔴 IT SAYS WHAT TO DO, NOT WHAT WENT WRONG. "Storage unavailable" means nothing
 *    to a normal person. Defined once here, on purpose: three funnels showing three
 *    different versions of this sentence is how the wording rots.
 *
 * The reason a blocked signup beats a warned-and-allowed one: the account would be
 * created server-side, the JWT could not be persisted, and THEIR EMAIL WOULD NOW BE
 * TAKEN. Fixing their browser and trying again returns `email_exists_error`, so
 * their only routes back are password reset or emailing support. A blocked signup is
 * a minor annoyance; a burnt email address is a lost user.
 */
export const STORAGE_BLOCKED_MESSAGE =
    "Your browser is blocking site data, so we can't keep you signed in. Turn it on for animates.app — or leave private browsing — and reload to create your account.";
